import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bookmark, Share2, Trash2, BookOpen } from "lucide-react";
import { toast } from "sonner";
import PageShell from "../components/PageShell";
import { getBookmarks, toggleBookmark } from "../lib/storage";
import { shareVerse } from "../lib/shareVerse";
import { useSettings } from "../context/SettingsContext";
import { track } from "../lib/analytics";

export default function SavedVerses() {
  const navigate = useNavigate();
  const { translation } = useSettings();
  const [items, setItems] = useState(() => [...getBookmarks()].reverse());

  const remove = (b) => {
    toggleBookmark({ book: b.book, bookName: b.bookName, chapter: b.chapter, verse: b.verse, text: b.text });
    setItems((prev) => prev.filter((x) => !(x.book === b.book && x.chapter === b.chapter && x.verse === b.verse)));
    toast("Removed from saved verses");
  };

  const share = async (b) => {
    const res = await shareVerse({ bookName: b.bookName, chapter: b.chapter, verse: b.verse, text: b.text, translation });
    if (res === "copied") toast("Verse copied to clipboard");
    if (res === "shared") track("verse_share", { ref: `${b.book}_${b.chapter}_${b.verse}` });
  };

  return (
    <PageShell
      title="Saved Verses"
      subtitle={items.length ? `${items.length} verse${items.length === 1 ? "" : "s"} you've saved` : "Your favorites live here"}
      icon={Bookmark}
      testid="saved-verses-page"
    >
      {items.length === 0 ? (
        <div className="mt-10 flex flex-col items-center gap-3 rounded-3xl border border-border bg-card/60 p-10 text-center backdrop-blur-md" data-testid="saved-empty">
          <Bookmark className="h-8 w-8 text-primary" />
          <p className="font-serif-display text-xl">No saved verses yet</p>
          <p className="font-ui max-w-sm text-sm text-muted-foreground">
            While reading, tap a verse and press the bookmark icon to save it here. You can revisit, share, or remove it any time.
          </p>
          <button
            onClick={() => navigate("/read/JHN/1")}
            data-testid="saved-start-reading"
            className="font-ui mt-2 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-transform hover:scale-105"
          >
            <BookOpen className="h-4 w-4" /> Start reading
          </button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="saved-list">
          {items.map((b) => (
            <div
              key={`${b.book}_${b.chapter}_${b.verse}`}
              data-testid={`saved-item-${b.book}-${b.chapter}-${b.verse}`}
              className="rounded-2xl border border-border bg-card/70 p-5 backdrop-blur-md transition-colors hover:border-primary/40"
            >
              <button
                onClick={() => navigate(`/read/${b.book}/${b.chapter}`)}
                className="block w-full text-left"
                data-testid={`saved-open-${b.book}-${b.chapter}-${b.verse}`}
              >
                <p className="reading-text text-base leading-relaxed text-foreground/90">{b.text}</p>
                <p className="font-ui mt-2 text-sm font-semibold text-primary">
                  {b.bookName} {b.chapter}:{b.verse}
                </p>
              </button>
              <div className="mt-3 flex items-center gap-2">
                <button
                  onClick={() => share(b)}
                  data-testid={`saved-share-${b.book}-${b.chapter}-${b.verse}`}
                  className="font-ui inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:bg-muted"
                >
                  <Share2 className="h-3.5 w-3.5" /> Share
                </button>
                <button
                  onClick={() => remove(b)}
                  data-testid={`saved-remove-${b.book}-${b.chapter}-${b.verse}`}
                  className="font-ui inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </PageShell>
  );
}
